/**
 * G2eGente - LNT - Levantamento de Necessidade de Treinamento
 * Lista automática de treinamentos necessários baseado em vencimentos e requisitos
 */
import React, { useState, useEffect } from 'react';
import { lntAPI, turmasAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { 
  AlertTriangle, 
  AlertCircle,
  Clock,
  Users,
  Loader2,
  ChevronDown,
  ChevronRight,
  Settings,
  FileDown,
  Plus,
  CheckSquare,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORIDADE_CONFIG = {
  'alta': { 
    label: 'Urgente', 
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: AlertTriangle,
    iconColor: 'text-red-600'
  },
  'media': { 
    label: 'Atenção', 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
    iconColor: 'text-amber-600'
  }
};

const MOTIVO_FILTER_OPTIONS = {
  'all': 'Todos os Motivos',
  'vencido': 'Vencidos',
  'vencendo': 'Vencendo',
  'nao_possui': 'Não Possui'
};

export default function LNTTab({ onCreateTurma }) {
  const [loading, setLoading] = useState(true);
  const [lntData, setLntData] = useState(null);
  const [expandedTraining, setExpandedTraining] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [prioridadeFilter, setPrioridadeFilter] = useState('all');
  const [motivoFilter, setMotivoFilter] = useState('all');
  
  // Config modal
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [diasAtencao, setDiasAtencao] = useState(30);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Create turma modal
  const [showCreateTurmaModal, setShowCreateTurmaModal] = useState(false);
  const [turmaTraining, setTurmaTraining] = useState(null);
  const [creatingTurma, setCreatingTurma] = useState(false);
  
  // Renovação modal
  const [showRenovacaoModal, setShowRenovacaoModal] = useState(false);
  const [tipoRenovacao, setTipoRenovacao] = useState('reciclagem');
  const [creatingRenovacao, setCreatingRenovacao] = useState(false);

  useEffect(() => {
    fetchLNT();
    fetchConfig();
  }, []);

  const fetchLNT = async () => {
    setLoading(true);
    try {
      const res = await lntAPI.get({ dias_atencao: diasAtencao });
      setLntData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar LNT');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await lntAPI.getConfig();
      setDiasAtencao(res.data.dias_atencao || 30);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await lntAPI.updateConfig({ dias_atencao: diasAtencao });
      toast.success('Configuração salva');
      setShowConfigModal(false);
      fetchLNT();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleToggleExpand = (trainingNome) => {
    if (expandedTraining === trainingNome) {
      setExpandedTraining(null);
    } else {
      setExpandedTraining(trainingNome);
      // Initialize selection for this training
      if (!selectedEmployees[trainingNome]) {
        setSelectedEmployees(prev => ({
          ...prev,
          [trainingNome]: []
        }));
      }
    }
  };

  const handleToggleEmployee = (trainingNome, employeeId) => {
    setSelectedEmployees(prev => {
      const current = prev[trainingNome] || [];
      if (current.includes(employeeId)) {
        return {
          ...prev,
          [trainingNome]: current.filter(id => id !== employeeId)
        };
      } else {
        return {
          ...prev,
          [trainingNome]: [...current, employeeId]
        };
      }
    });
  };

  const handleSelectAll = (trainingNome, funcionarios) => {
    const allIds = funcionarios.map(f => f.employee_id);
    const currentSelected = selectedEmployees[trainingNome] || [];
    
    if (currentSelected.length === allIds.length) {
      // Deselect all
      setSelectedEmployees(prev => ({
        ...prev,
        [trainingNome]: []
      }));
    } else {
      // Select all
      setSelectedEmployees(prev => ({
        ...prev,
        [trainingNome]: allIds
      }));
    }
  };

  const handleOpenCreateTurma = (training) => {
    setTurmaTraining(training);
    // Verificar se há funcionários com treinamento vencido/vencendo
    const temVencidos = training.funcionarios.some(f => 
      f.motivo === 'Vencido' || f.motivo === 'Vencendo'
    );
    if (temVencidos) {
      setShowRenovacaoModal(true);
    } else {
      setShowCreateTurmaModal(true);
    }
  };

  const handleCreateRenovacao = async () => {
    if (!turmaTraining?.treinamento_id) {
      toast.error('Treinamento não cadastrado no sistema. Cadastre primeiro em Treinamentos.');
      return;
    }
    
    setCreatingRenovacao(true);
    try {
      // Criar turma de renovação via endpoint específico
      const res = await lntAPI.criarTurmaRenovacao({
        training_id: turmaTraining.treinamento_id,
        tipo: tipoRenovacao
      });
      
      const turmaId = res.data.turma.id;
      const participantes = res.data.participantes_sugeridos;
      
      // Adicionar participantes selecionados ou todos os sugeridos
      const selected = selectedEmployees[turmaTraining.treinamento_nome] || [];
      const participantesToAdd = selected.length > 0 
        ? participantes.filter(p => selected.includes(p.employee_id))
        : participantes;
      
      for (const p of participantesToAdd) {
        try {
          await turmasAPI.addParticipante(turmaId, { 
            employee_id: p.employee_id,
            tipo_participacao: p.tipo_participacao
          });
        } catch (err) {
          console.error('Error adding participant:', p.employee_id, err);
        }
      }
      
      const tipoLabel = tipoRenovacao === 'reciclagem' ? 'Reciclagem' : 'Novo Treinamento';
      toast.success(`Turma de ${tipoLabel} criada com ${participantesToAdd.length} participante(s)`);
      
      setShowRenovacaoModal(false);
      setSelectedEmployees(prev => ({
        ...prev,
        [turmaTraining.treinamento_nome]: []
      }));
      
      // Refresh LNT
      fetchLNT();
      
    } catch (error) {
      console.error('Error creating renovacao turma:', error);
      toast.error('Erro ao criar turma de renovação');
    } finally {
      setCreatingRenovacao(false);
    }
  };

  const handleCreateTurma = async () => {
    if (!turmaTraining?.treinamento_id) {
      toast.error('Treinamento não cadastrado no sistema. Cadastre primeiro em Treinamentos.');
      return;
    }
    
    const selected = selectedEmployees[turmaTraining.treinamento_nome] || [];
    if (selected.length === 0) {
      toast.error('Selecione pelo menos um funcionário');
      return;
    }
    
    setCreatingTurma(true);
    try {
      // Create turma
      const turmaRes = await turmasAPI.create({
        training_id: turmaTraining.treinamento_id,
        nome: `${turmaTraining.treinamento_nome} - LNT ${new Date().toLocaleDateString('pt-BR')}`,
        capacidade_maxima: Math.max(30, selected.length + 5)
      });
      
      // Add selected participants
      for (const empId of selected) {
        try {
          await turmasAPI.addParticipante(turmaRes.data.id, { employee_id: empId });
        } catch (err) {
          console.error('Error adding participant:', empId, err);
        }
      }
      
      toast.success(`Turma criada com ${selected.length} participante(s)`);
      setShowCreateTurmaModal(false);
      setSelectedEmployees(prev => ({
        ...prev,
        [turmaTraining.treinamento_nome]: []
      }));
      
      // Optionally refresh LNT
      // fetchLNT();
      
    } catch (error) {
      console.error('Error creating turma:', error);
      toast.error('Erro ao criar turma');
    } finally {
      setCreatingTurma(false);
    }
  };

  const handleExportPDF = async () => {
    if (!lntData || lntData.por_treinamento.length === 0) {
      toast.warning('Não há dados para exportar');
      return;
    }
    
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let y = 20;
      
      const checkPageBreak = (requiredSpace) => {
        if (y + requiredSpace > pageHeight - 20) {
          pdf.addPage();
          y = 20;
          return true;
        }
        return false;
      };
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text('LEVANTAMENTO DE NECESSIDADE DE TREINAMENTO', pageWidth / 2, y, { align: 'center' });
      y += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, pageWidth / 2, y, { align: 'center' });
      y += 10;
      
      // Summary
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, y, contentWidth, 15, 'F');
      y += 10;
      pdf.setFontSize(10);
      pdf.setTextColor(50, 50, 50);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Urgentes: ${lntData.resumo.urgentes}`, margin + 10, y);
      pdf.text(`Atenção: ${lntData.resumo.atencao}`, margin + 50, y);
      pdf.text(`Funcionários: ${lntData.resumo.funcionarios_afetados}`, margin + 90, y);
      pdf.text(`Treinamentos: ${lntData.resumo.treinamentos_distintos}`, margin + 140, y);
      y += 15;
      
      // Trainings
      for (const training of lntData.por_treinamento) {
        checkPageBreak(30);
        
        // Training header
        pdf.setFillColor(6, 182, 212);
        pdf.rect(margin, y, contentWidth, 8, 'F');
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${training.treinamento_nome} (${training.funcionarios.length} pessoas)`, margin + 3, y + 5.5);
        y += 12;
        
        // Table header
        pdf.setFillColor(230, 230, 230);
        pdf.rect(margin, y, contentWidth, 6, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(50, 50, 50);
        pdf.text('Nome', margin + 3, y + 4);
        pdf.text('Cargo', margin + 55, y + 4);
        pdf.text('Setor', margin + 100, y + 4);
        pdf.text('Motivo', margin + 140, y + 4);
        pdf.text('Prioridade', margin + 165, y + 4);
        y += 8;
        
        // Table rows
        pdf.setFont('helvetica', 'normal');
        for (const func of training.funcionarios) {
          checkPageBreak(6);
          
          pdf.setFontSize(8);
          pdf.text(func.employee_nome?.substring(0, 25) || '-', margin + 3, y + 3);
          pdf.text(func.cargo?.substring(0, 20) || '-', margin + 55, y + 3);
          pdf.text(func.departamento?.substring(0, 18) || '-', margin + 100, y + 3);
          pdf.text(func.motivo || '-', margin + 140, y + 3);
          pdf.text(func.prioridade === 'alta' ? 'URGENTE' : 'ATENÇÃO', margin + 165, y + 3);
          
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, y + 5, pageWidth - margin, y + 5);
          y += 6;
        }
        
        y += 5;
      }
      
      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 30, pageHeight - 10);
        pdf.text('G2eGente - LNT', margin, pageHeight - 10);
      }
      
      pdf.save(`LNT_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  // Filter trainings by priority and motivo
  const filteredTrainings = lntData?.por_treinamento?.filter(t => {
    // Filtro de prioridade
    if (prioridadeFilter === 'alta' && t.total_alta === 0) return false;
    if (prioridadeFilter === 'media' && (t.total_media === 0 || t.total_alta > 0)) return false;
    
    // Filtro de motivo
    if (motivoFilter !== 'all') {
      const hasMotivo = t.funcionarios.some(f => {
        if (motivoFilter === 'vencido') return f.motivo === 'Vencido';
        if (motivoFilter === 'vencendo') return f.motivo === 'Vencendo';
        if (motivoFilter === 'nao_possui') return f.motivo?.includes('Não possui');
        return true;
      });
      if (!hasMotivo) return false;
    }
    
    return true;
  }).map(t => {
    // Se tem filtro de motivo, filtrar também os funcionários dentro de cada treinamento
    if (motivoFilter === 'all') return t;
    
    const filteredFuncionarios = t.funcionarios.filter(f => {
      if (motivoFilter === 'vencido') return f.motivo === 'Vencido';
      if (motivoFilter === 'vencendo') return f.motivo === 'Vencendo';
      if (motivoFilter === 'nao_possui') return f.motivo?.includes('Não possui');
      return true;
    });
    
    return {
      ...t,
      funcionarios: filteredFuncionarios,
      total_alta: filteredFuncionarios.filter(f => f.prioridade === 'alta').length,
      total_media: filteredFuncionarios.filter(f => f.prioridade === 'media').length
    };
  }).filter(t => t.funcionarios.length > 0) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={prioridadeFilter} onValueChange={setPrioridadeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar prioridade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as Prioridades</SelectItem>
              <SelectItem value="alta">Apenas Urgentes</SelectItem>
              <SelectItem value="media">Apenas Atenção</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={motivoFilter} onValueChange={setMotivoFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar motivo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Motivos</SelectItem>
              <SelectItem value="vencido">🔴 Vencidos</SelectItem>
              <SelectItem value="vencendo">🟡 Vencendo</SelectItem>
              <SelectItem value="nao_possui">🔵 Não Possui</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConfigModal(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Configurar
          </Button>
          <Button variant="outline" onClick={handleExportPDF}>
            <FileDown className="w-4 h-4 mr-2" />
            Exportar PDF
          </Button>
          <Button variant="outline" onClick={fetchLNT}>
            Atualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {lntData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                <div>
                  <div className="text-2xl font-bold text-red-700">{lntData.resumo.urgentes}</div>
                  <div className="text-sm text-red-600">Urgentes</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-600" />
                <div>
                  <div className="text-2xl font-bold text-amber-700">{lntData.resumo.atencao}</div>
                  <div className="text-sm text-amber-600">Atenção</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-slate-600" />
                <div>
                  <div className="text-2xl font-bold text-slate-900">{lntData.resumo.funcionarios_afetados}</div>
                  <div className="text-sm text-slate-500">Funcionários</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-cyan-600" />
                <div>
                  <div className="text-2xl font-bold text-slate-900">{lntData.resumo.treinamentos_distintos}</div>
                  <div className="text-sm text-slate-500">Treinamentos</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info about period */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700">
          <strong>Período de atenção:</strong> Treinamentos vencendo nos próximos <strong>{diasAtencao} dias</strong> aparecem com status "Atenção" (🟡)
        </p>
      </div>

      {/* Training List */}
      {filteredTrainings.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">
              Nenhuma necessidade de treinamento identificada
            </h3>
            <p className="text-slate-500 mt-2">
              Todos os funcionários estão com seus treinamentos em dia!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTrainings.map((training) => {
            const isExpanded = expandedTraining === training.treinamento_nome;
            const selected = selectedEmployees[training.treinamento_nome] || [];
            
            return (
              <Card key={training.treinamento_nome} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => handleToggleExpand(training.treinamento_nome)}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {training.treinamento_nome}
                              {training.categoria && (
                                <Badge variant="outline" className="text-xs font-normal">
                                  {training.categoria}
                                </Badge>
                              )}
                            </CardTitle>
                            <p className="text-sm text-slate-500 mt-1">
                              {training.funcionarios.length} funcionário(s) precisam deste treinamento
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {training.total_alta > 0 && (
                            <Badge className="bg-red-100 text-red-700 border border-red-200">
                              {training.total_alta} urgente(s)
                            </Badge>
                          )}
                          {training.total_media > 0 && (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                              {training.total_media} atenção
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0 border-t">
                      {/* Actions */}
                      <div className="flex items-center justify-between py-3 mb-3 border-b">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={selected.length === training.funcionarios.length && training.funcionarios.length > 0}
                            onCheckedChange={() => handleSelectAll(training.treinamento_nome, training.funcionarios)}
                          />
                          <span className="text-sm text-slate-600">
                            {selected.length > 0 
                              ? `${selected.length} selecionado(s)` 
                              : 'Selecionar todos'
                            }
                          </span>
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={() => handleOpenCreateTurma(training)}
                          disabled={selected.length === 0}
                          className="bg-cyan-600 hover:bg-cyan-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Criar Turma ({selected.length})
                        </Button>
                      </div>
                      
                      {/* Employees Table */}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40px]"></TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Cargo</TableHead>
                            <TableHead>Setor</TableHead>
                            <TableHead>Gestor</TableHead>
                            <TableHead>Motivo</TableHead>
                            <TableHead>Prioridade</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {training.funcionarios.map((func) => {
                            const PrioridadeIcon = PRIORIDADE_CONFIG[func.prioridade]?.icon || AlertCircle;
                            return (
                              <TableRow key={func.employee_id} className={
                                func.prioridade === 'alta' ? 'bg-red-50' : func.prioridade === 'media' ? 'bg-amber-50' : ''
                              }>
                                <TableCell>
                                  <Checkbox
                                    checked={selected.includes(func.employee_id)}
                                    onCheckedChange={() => handleToggleEmployee(training.treinamento_nome, func.employee_id)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{func.employee_nome}</TableCell>
                                <TableCell>{func.cargo || '-'}</TableCell>
                                <TableCell>{func.departamento || '-'}</TableCell>
                                <TableCell>{func.gestor_nome || '-'}</TableCell>
                                <TableCell>
                                  <span className="text-sm">
                                    {func.motivo}
                                    {func.data_vencimento && (
                                      <span className="text-slate-500 ml-1">({func.data_vencimento})</span>
                                    )}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Badge className={PRIORIDADE_CONFIG[func.prioridade]?.color}>
                                    <PrioridadeIcon className={`w-3 h-3 mr-1 ${PRIORIDADE_CONFIG[func.prioridade]?.iconColor}`} />
                                    {PRIORIDADE_CONFIG[func.prioridade]?.label}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      {/* Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-600" />
              Configurações do LNT
            </DialogTitle>
            <DialogDescription>
              Defina os parâmetros para o levantamento de necessidade de treinamento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Período de Atenção (dias)</Label>
              <p className="text-sm text-slate-500 mb-2">
                Treinamentos que vencerem dentro deste período aparecerão com status "Atenção" (🟡)
              </p>
              <Select value={String(diasAtencao)} onValueChange={(v) => setDiasAtencao(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="45">45 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig}>
              {savingConfig && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Turma Confirmation Modal */}
      <Dialog open={showCreateTurmaModal} onOpenChange={setShowCreateTurmaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-600" />
              Criar Turma
            </DialogTitle>
            <DialogDescription>
              Confirme a criação da turma de treinamento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900">{turmaTraining?.treinamento_nome}</h4>
              <p className="text-sm text-slate-600 mt-1">
                {(selectedEmployees[turmaTraining?.treinamento_nome] || []).length} funcionário(s) selecionado(s)
              </p>
            </div>

            {!turmaTraining?.treinamento_id && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Este treinamento não está cadastrado no sistema
                  </span>
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  Cadastre o treinamento primeiro na aba "Treinamentos" para poder criar turmas.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTurmaModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateTurma} 
              disabled={creatingTurma || !turmaTraining?.treinamento_id}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {creatingTurma && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar Turma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Renovação/Reciclagem */}
      <Dialog open={showRenovacaoModal} onOpenChange={setShowRenovacaoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-cyan-600" />
              Renovar Treinamento
            </DialogTitle>
            <DialogDescription>
              Este treinamento possui funcionários com treinamento vencido ou vencendo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900">{turmaTraining?.treinamento_nome}</h4>
              <div className="flex gap-2 mt-2">
                {turmaTraining?.funcionarios && (
                  <>
                    <Badge className="bg-red-100 text-red-700">
                      {turmaTraining.funcionarios.filter(f => f.motivo === 'Vencido').length} vencidos
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-700">
                      {turmaTraining.funcionarios.filter(f => f.motivo === 'Vencendo').length} vencendo
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-700">
                      {turmaTraining.funcionarios.filter(f => f.motivo?.includes('Não possui')).length} novos
                    </Badge>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">Tipo de Renovação</Label>
              <p className="text-sm text-slate-500 mb-3">
                Escolha o tipo de treinamento a ser criado
              </p>
              
              <div className="space-y-3">
                <label 
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    tipoRenovacao === 'reciclagem' 
                      ? 'border-cyan-500 bg-cyan-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setTipoRenovacao('reciclagem')}
                >
                  <input 
                    type="radio" 
                    name="tipoRenovacao" 
                    value="reciclagem"
                    checked={tipoRenovacao === 'reciclagem'}
                    onChange={() => setTipoRenovacao('reciclagem')}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Reciclagem</div>
                    <div className="text-sm text-slate-500">
                      Carga horária reduzida para funcionários que já realizaram o treinamento
                    </div>
                  </div>
                </label>
                
                <label 
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    tipoRenovacao === 'inicial' 
                      ? 'border-cyan-500 bg-cyan-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setTipoRenovacao('inicial')}
                >
                  <input 
                    type="radio" 
                    name="tipoRenovacao" 
                    value="inicial"
                    checked={tipoRenovacao === 'inicial'}
                    onChange={() => setTipoRenovacao('inicial')}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Novo Treinamento</div>
                    <div className="text-sm text-slate-500">
                      Carga horária completa (treinamento do zero)
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Importante:</strong> A turma será criada com todos os funcionários elegíveis 
                (vencidos, vencendo e novos). Você poderá ajustar a lista em "Gerenciar Turma".
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenovacaoModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateRenovacao} 
              disabled={creatingRenovacao || !turmaTraining?.treinamento_id}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {creatingRenovacao && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <RefreshCw className="w-4 h-4 mr-2" />
              Criar Turma de {tipoRenovacao === 'reciclagem' ? 'Reciclagem' : 'Treinamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
